import { useState, SyntheticEvent, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cs from "classnames";

import useDetectOutsideClick from "../../../../hooks/useOutsideDetectClick";
import { SelectProfilesInterface } from "../inputTypes";
import SelectSearchInput from "./SelectSearchInput";
import InputOutline from "../InputOutline";

import { ReactComponent as Arrow } from "../../../../assets/icons/arrow.svg";
import styled, { css } from "styled-components";
import { ISelectOption } from "types/globalTypes";

const SelectWrapper = styled.div.attrs<{
  $customStyle?: React.CSSProperties;
  $width?: string;
}>(({ $customStyle, $width }) => ({
  style: {
    ...$customStyle,
    ...($width ? { width: $width } : {}),
  },
}))<{ $customStyle?: React.CSSProperties; $width?: string }>`
  text-align: left;
  width: ${({ $width }) => $width || "280px"};
  min-width: 0;
  position: relative;
  font-size: 0.875rem;
`;

const SelectInput = styled.div<{
  $hasValue: boolean;
  $hasError: boolean;
  $disabled: boolean;
  $small: boolean;
  $isActive: boolean;
}>`
  width: 100%;
  min-width: 0;
  min-height: ${({ $small }) => ($small ? "32px" : "48px")};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ $small }) => ($small ? "6px 35px 6px 18px" : "12px 35px 12px 18px")};
  background: transparent;
  font-size: 15px;
  letter-spacing: 0;
  font-weight: 500;
  cursor: pointer;
  text-align: right;
  position: relative;

  background: ${({ $small }) => ($small ? "transparent" : "rgba(156, 176, 196, 0.11)")};

  border: ${({ $small }) =>
    $small ? "1px solid rgba(163,158,157,0.41)" : "1px solid rgba(156, 176, 196, 0.15)"};

  color: ${({ $small }) => ($small ? "white" : "#9cb0c4")};

  fill: ${({ $small }) => ($small ? "white" : "#9cb0c4")};

  transition: all 0.15s ease-in-out;

  &:hover {
    color: white;
    fill: white;
    background: rgba(156, 176, 196, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: white;
      fill: white;
      /* background: rgba(156, 176, 196, 0.42); */
      border: 1px solid rgba(255, 255, 255, 0.48);
    `}

  ${({ $hasValue }) =>
    $hasValue &&
    css`
      &.formControl--hasValue {
      }
    `}

  ${({ $hasError }) =>
    $hasError &&
    css`
      &.formControl--hasError {
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      &.formControl--disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    `}
`;

const SelectValue = styled.div`
  padding-right: 10px;
`;

const SelectLabel = styled.label<{ $labelColor?: string; $small?: boolean }>`
  position: absolute;
  top: 7px;
  left: 9px;
  font-size: 11px;
  letter-spacing: 0;
  font-weight: 400;
  color: ${({ $labelColor, $small }) => ($small ? "white" : $labelColor || "#9CB0C4")};
  transition: color 0.15s ease-in-out;

  .formControl:hover & {
    color: white;
  }

  .formControl.formControl--active & {
    color: white;
  }
`;

const ArrowContainer = styled.div<{ $isActive: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  right: 12px;

  & > svg {
    width: 20px;
    transition: transform 0.15s ease-in-out;
    transform: ${({ $isActive }) => ($isActive ? "rotate(90deg)" : "rotate(-90deg)")};
  }
`;

const OptionsContainer = styled.div<{ $isActive: boolean }>`
  display: ${({ $isActive }) => ($isActive ? "flex" : "none")};
  flex-direction: column;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  position: absolute;
  z-index: 40;
  left: 0;
  right: 0;
  cursor: pointer;
  font-size: 0.875rem;
  border-radius: 8px;
  color: black;
  font-weight: 400;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const OptionsList = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 208px;
  position: relative;
`;

const OptionItem = styled.div<{
  $isActive: boolean;
  $isFirst: boolean;
  $isLast: boolean;
  $index: number;
}>`
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  background-color: ${({ $index }) => ($index % 2 === 0 ? "#9cb0c4c4" : "#83a3c4df")};
  border-top-left-radius: ${({ $isFirst }) => ($isFirst ? "8px" : "0px")};
  border-top-right-radius: ${({ $isFirst }) => ($isFirst ? "8px" : "0px")};
  border-bottom-left-radius: ${({ $isLast }) => ($isLast ? "8px" : "0px")};
  border-bottom-right-radius: ${({ $isLast }) => ($isLast ? "8px" : "0px")};
  transition: all 0.15s ease-in-out;

  &:hover {
    color: black;
    /* opacity: 0.9; */
    /* background-color: ${({ $index }) => ($index % 2 === 0 ? "#9cb0c4" : "#83a4c4")}; */
    background-color: #ffffff;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: #3b82f6;
      color: white;

      &:hover {
        background-color: #2563eb;
        color: white;
      }
    `}
`;

const NoResults = styled.div`
  font-size: 0.75rem;
  padding: 8px 18px;
  color: #666;
  text-align: center;
`;

const LoadMoreHint = styled.div`
  font-size: 0.75rem;
  padding: 8px 18px;
  color: #666;
  text-align: center;
`;

const NewSelectStatystics = <T,>({
  width,
  className = "",
  disabled,
  defaultValue,
  error,
  helperText,
  isSearchable,
  items: providedItems,
  label,
  name,
  onlyPickValue,
  placeholder,
  showErrors = true,
  style = {},
  optionClassName = "",
  wrapperClassName = "",
  small = false,
  labelColor,
  selectedItem,
  disableSort,
  setSelectedItem,
  onChange,
  onLoadMore,
  hasMoreItems = false,
  isLoadingMore = false,
  ...props
}: SelectProfilesInterface<T>): JSX.Element => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("common.select");
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const selectBackgroundRef = useRef(null);

  const searchQueryHasChanged = useRef(false);
  const itemsListRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);
  const [backgroundWidth, setBackgroundWidth] = useState(
    // @ts-ignore
    selectBackgroundRef?.current?.offsetWidth
  );
  const items = useMemo<Array<ISelectOption<T>>>(
    () => (Array.isArray(providedItems) ? providedItems : []),
    [providedItems]
  );

  // const filteredItems = useMemo(
  //   () =>
  //     items
  //       .filter((item) => {
  //         if (!item || typeof item.label !== "string") return false;
  //         if (!isSearchable) return true;
  //         return item.label
  //           .toLocaleLowerCase()
  //           .includes(searchQuery.toLocaleLowerCase());
  //       })
  //       .sort((a, b) => a.label.localeCompare(b.label)),
  //   [isSearchable, items, searchQuery],
  // );

  const filteredItems = useMemo(() => {
    const base = items.filter((item) => {
      if (!item || typeof item.label !== "string") return false;
      if (!isSearchable) return true;
      return item.label.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase());
    });

    if (disableSort) return base;

    return [...base].sort((a, b) => a.label.localeCompare(b.label));
  }, [isSearchable, items, searchQuery, disableSort]);

  useEffect(() => {
    if (selectBackgroundRef.current) {
      // @ts-ignore
      const width = selectBackgroundRef?.current?.offsetWidth;
      setBackgroundWidth(width);
    }
  }, [selectedItem]);

  const showError = !!error;

  const isDisabled = !!disabled;

  const handleFocus = () => {
    if (isDisabled) return;
    setIsActive(!isActive);
  };

  const handleBlur = useCallback(
    (e: SyntheticEvent) => {
      if (isDisabled) return;
      setIsActive(false);
    },
    [isDisabled]
  );

  const handleChange = (item: ISelectOption<T>) => {
    if (isDisabled) return;
    setSelectedItem(item);
  };

  const handleSearchChange = (query: string) => {
    if (isDisabled) return;
    searchQueryHasChanged.current = true;
    setSearchQuery(query);
  };

  const handleOptionsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isDisabled || !onLoadMore || !hasMoreItems || isLoadingMore) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom > 48) {
      loadMoreLockRef.current = false;
      return;
    }

    if (loadMoreLockRef.current) {
      return;
    }

    loadMoreLockRef.current = true;
    onLoadMore();
  };

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreLockRef.current = false;
    }
  }, [isLoadingMore]);

  const highlightItem = (index: number) => {
    if (index < 0 || index > filteredItems.length) return;
    setActiveItemIndex(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    const index = activeItemIndex || 0;

    switch (e.key) {
      case "ArrowDown":
        highlightItem(index + 1);
        break;
      case "ArrowUp":
        highlightItem(index - 1);
        break;
      case "Enter":
        e.preventDefault();
        if (activeItemIndex !== null) {
          handleChange(filteredItems[activeItemIndex]);
          setIsActive(false);
        }
        setActiveItemIndex(null);
        break;

      default:
        break;
    }
  };

  useDetectOutsideClick(selectRef, handleBlur);

  useEffect(() => {
    if (defaultValue === undefined || items.length === 0) {
      return;
    }

    const selected = items.find((item) => item.value === defaultValue);
    setSelectedItem(selected || null);
  }, [defaultValue, items]);

  useEffect(() => {
    if (onChange && selectedItem) {
      onChange(selectedItem);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (activeItemIndex === null || !itemsListRef.current) {
      return;
    }
    const activeNode = itemsListRef.current.children[activeItemIndex];
    activeNode.scrollIntoView({
      block: "nearest",
    });
  }, [activeItemIndex]);

  useEffect(() => {
    if (searchQueryHasChanged.current) {
      setActiveItemIndex(0);
    }
  }, [filteredItems]);

  const hasValue = Boolean(!onlyPickValue && (!!selectedItem || resolvedPlaceholder));
  const isLastIndex = (idx: number) => idx === filteredItems.length - 1;

  return (
    <SelectWrapper
      className={wrapperClassName}
      $customStyle={style}
      $width={width}
      ref={selectRef}
      onKeyDown={handleKeyDown}
    >
      <SelectInput
        aria-disabled={isDisabled}
        onClick={handleFocus}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cs("formControl", {
          "formControl--hasValue": hasValue,
          "formControl--hasError": showError,
          "formControl--disabled": isDisabled,
          "formControl--active": isActive,
          [className]: className,
        })}
        data-testid="selectInput"
        $hasValue={hasValue}
        $hasError={showError}
        $disabled={isDisabled}
        $small={small}
        $isActive={isActive}
      >
        <InputOutline
          name={name}
          label={label}
          backgroundWidth={backgroundWidth}
          selectedItem={selectedItem}
          LabelComponent={SelectLabel}
          labelComponentProps={{ $labelColor: labelColor, $small: small }}
        />
        <SelectValue
          data-testid="selectValue"
          ref={selectBackgroundRef}
          className={optionClassName}
        >
          {!onlyPickValue && (selectedItem?.label || resolvedPlaceholder)}
        </SelectValue>
        <ArrowContainer $isActive={isActive}>
          <Arrow />
        </ArrowContainer>
      </SelectInput>
      <OptionsContainer data-testid="selectOptions" $isActive={isActive}>
        {isSearchable && (
          <SelectSearchInput isActive={isActive} onQueryChange={handleSearchChange} />
        )}
        <OptionsList ref={itemsListRef} onScroll={handleOptionsScroll}>
          {filteredItems.length ? (
            filteredItems.map((item, idx) => (
              <OptionItem
                key={`${item.label}${item.value}`}
                data-index={idx}
                className={optionClassName}
                $isActive={activeItemIndex === idx}
                $isFirst={idx === 0}
                $isLast={isLastIndex(idx) && !hasMoreItems && !isLoadingMore}
                $index={idx}
                onClick={(e) => {
                  handleChange(item);
                  handleBlur(e);
                }}
              >
                {item.label}
              </OptionItem>
            ))
          ) : (
            <NoResults>No results</NoResults>
          )}
          {isLoadingMore && <LoadMoreHint>{t("common.loading")}</LoadMoreHint>}
        </OptionsList>
      </OptionsContainer>
    </SelectWrapper>
  );
};

export default NewSelectStatystics;
